import { m } from "framer-motion";
import { Sparkles, Shield, DollarSign, Users, Clock, Award } from "lucide-react";

const reasons = [
  {
    icon: Sparkles,
    title: "Cleaning & Painting In One Call",
    desc: "Most companies do one or the other. We handle the deep clean, the repaint, and the pressure wash — so you're not juggling three crews and three schedules.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Licensed & Insured",
    desc: "Yessica Cleaning and Painting LLC is a registered Indiana LLC, fully insured. Your home and business are protected on every job.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Honest, Transparent Pricing",
    desc: "Free written estimates with no hidden fees, and returning customers save 25% on their next service.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: "Mon – Fri · 7 AM – 5:30 PM",
    desc: "Early starts and full days mean most jobs get finished faster. Commercial customers can ask about scheduling outside business hours.",
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Prep Work Done Right",
    desc: "We wash, scrape, sand, caulk, and prime before a drop of paint goes on. That prep is why our finishes last instead of peeling.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Bilingual, Family-Run",
    desc: "Owner Yessica La O Gomez personally backs every job. Friendly service in English and Spanish.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why Yessica Cleaning & Painting
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons families and businesses across Northern Indiana and Southwest Michigan
            trust us with their homes and properties.
          </m.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <m.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
